// Engineering & business case report for a territory plan.
// Reads ?planId=X&margin=...&build=...&aerialMix=... from URL.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

const fmtUSD = (n: number) =>
  !isFinite(n) ? '—'
  : Math.abs(n) >= 1_000_000 ? `${n < 0 ? '-' : ''}$${(Math.abs(n) / 1_000_000).toFixed(2)}M`
  : Math.abs(n) >= 1_000     ? `${n < 0 ? '-' : ''}$${(Math.abs(n) / 1_000).toFixed(0)}k`
  :                            `${n < 0 ? '-' : ''}$${Math.round(Math.abs(n)).toLocaleString()}`;
const fmt2 = (n: number) => isFinite(n) ? `$${n.toFixed(2)}` : '—';
const fmtNum = (n: number) => Math.round(n).toLocaleString();
const fmtMo = (n: number | null) =>
  n == null || !isFinite(n) ? '—' : n < 12 ? `${n.toFixed(1)} mo` : `${(n / 12).toFixed(1)} yr`;

const MODE_COLOR: Record<string, string> = {
  fiber_build: '#f97316',
  wireless_ptp: '#0ea5e9',
  ride_along: '#10b981',
};
const MODE_LABEL: Record<string, string> = {
  fiber_build: 'Fiber build',
  wireless_ptp: 'Wireless',
  ride_along: 'Ride-along',
};

const SECTION_H: React.CSSProperties = {
  marginTop: 28, fontSize: 13, textTransform: 'uppercase', letterSpacing: '.08em',
  color: '#1e293b', borderBottom: '1.5px solid #1e293b', paddingBottom: 4, fontWeight: 700,
};
const SUB_H: React.CSSProperties = {
  marginTop: 18, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em',
  color: '#475569', borderBottom: '1px dashed #cbd5e1', paddingBottom: 3, fontWeight: 700,
};

interface SoilProfile {
  weighted_by?: 'area' | 'route' | 'area_fallback';
  route_length_m?: number | null;
  aerial_per_ft?: number | null;
  rate_sources?: Record<string, string | null>;
  input?: { kind?: string; intersected_area_m2?: number };
  soil_profile: {
    dominant_drainage: string | null;
    dominant_hydro_group: string | null;
    avg_slope_pct: number | null;
    avg_bedrock_depth_cm: number | null;
    hydric_pct_weighted: number | null;
    map_unit_count: number;
    top_soils: Array<{ musym: string; muname: string; area_pct: number }>;
  } | null;
  construction_method_breakdown: Record<string, { pct: number; labor_per_ft: number }> | null;
  blended_labor_per_ft?: number;
  catalog_default_per_ft?: number;
  wetland_risk: { hydric_majority_area_pct: number; hydric_polygon_count: number; needs_404_review: boolean } | null;
}

export default function ReportsPage() {
  const [planId, setPlanId] = useState<string | null>(null);
  const [margin, setMargin] = useState<number>(0.72);
  const [aerialMixPct, setAerialMixPct] = useState<number>(50);
  const [takeRateUnservedPct, setTakeRateUnservedPct] = useState<number>(60);
  const [takeRateServedPct,   setTakeRateServedPct]   = useState<number>(25);
  // back-compat: old reports passed a single takeRate; if present, use it
  // for both segments so historical share-links still work
  const [legacyTakeRatePct, setLegacyTakeRatePct] = useState<number | null>(null);
  const [buildFilter, setBuildFilter] = useState<Set<number> | null>(null);
  const [planIds, setPlanIds] = useState<string[]>([]);
  // 2026-05-30: report-config params from URL (Phase 2.5)
  const [audience, setAudience] = useState<string>('business');
  const [grantDisplay, setGrantDisplay] = useState<string>('separate');
  const [discountRate, setDiscountRate] = useState<number>(8);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPlanId(params.get('planId'));
    const multi = params.get('planIds');
    if (multi) setPlanIds(multi.split(',').filter(Boolean));
    const m = parseFloat(params.get('margin') ?? '');
    if (!isNaN(m) && m > 0 && m < 1) setMargin(m);
    const am = parseFloat(params.get('aerialMix') ?? '');
    if (!isNaN(am) && am >= 0 && am <= 100) setAerialMixPct(am);
    const trU = parseFloat(params.get('takeRateUnserved') ?? '');
    if (!isNaN(trU) && trU > 0 && trU <= 100) setTakeRateUnservedPct(trU);
    const trS = parseFloat(params.get('takeRateServed') ?? '');
    if (!isNaN(trS) && trS > 0 && trS <= 100) setTakeRateServedPct(trS);
    const trLegacy = parseFloat(params.get('takeRate') ?? '');
    if (!isNaN(trLegacy) && trLegacy > 0 && trLegacy <= 100) {
      setLegacyTakeRatePct(trLegacy);
      if (isNaN(trU)) setTakeRateUnservedPct(trLegacy);
      if (isNaN(trS)) setTakeRateServedPct(trLegacy);
    }
    const b = params.get('build');
    if (b) setBuildFilter(new Set(b.split(',').filter(Boolean).map((x) => parseInt(x, 10))));
  }, []);

  const { data: plan, isLoading } = useQuery<any>({
    queryKey: ['territory-plan-report', planId],
    queryFn: () => api.get(`/territory/plans/${planId}`),
    enabled: !!planId,
  });

  // 2026-05-30 Phase 2.5: RDOF summary for combined-subsidy tile.
  const rdofSummaryQ = useQuery<{
    total_rdof_passings: number;
    total_support_10yr_usd: number;
    total_monthly_support_usd: number;
    per_state: any[];
  }>({
    queryKey: ['rdof-summary-report', planId],
    queryFn: () => api.get(`/territory/plans/${planId}/rdof-summary`),
    enabled: !!planId, staleTime: 10 * 60 * 1000, retry: 0,
  });
  // Pull BEAD-priced totals at BOTH slider extremes — same trick the
  // Builder uses — so we can interpolate to the user's current aerial mix
  // without an extra API hit. Construction labor is the only line that
  // varies with the slider; everything else is constant across the two runs.
  const { data: pmAerial } = useQuery<any>({
    queryKey: ['pricing-model-report', planId, 'aerial'],
    queryFn: () => api.get(`/territory/plans/${planId}/pricing-model?aerialPct=100&takeRate=40`),
    enabled: !!planId, staleTime: 10 * 60 * 1000, retry: 1,
  });
  const { data: pmBuried } = useQuery<any>({
    queryKey: ['pricing-model-report', planId, 'buried'],
    queryFn: () => api.get(`/territory/plans/${planId}/pricing-model?aerialPct=0&takeRate=40`),
    enabled: !!planId, staleTime: 10 * 60 * 1000, retry: 1,
  });
  const { data: economics } = useQuery<any>({
    queryKey: ['plant-economics'],
    queryFn: () => api.get('/settings/economics').catch(() => null),
    enabled: !!planId, staleTime: 60 * 60 * 1000, retry: 0,
  });

  const { data: soil } = useQuery<SoilProfile | null>({
    queryKey: ['soil-profile-report', planId],
    queryFn: async () => {
      try { return await api.get(`/geohub-data/soil-profile?territory_plan_id=${planId}&weighted_by=route`); }
      catch { return null; }
    },
    enabled: !!planId, staleTime: 10 * 60 * 1000, retry: 0,
  });

  if (planIds.length >= 1 && !planId) {
    return <ScenarioComparison planIds={planIds} margin={margin} />;
  }

  if (!planId) {
    return (
      <>
        <div className="page__header">
          <div>
            <h1 className="page__title">Reports</h1>
            <div className="page__subtitle">Engineering & business case briefs</div>
          </div>
        </div>
        <div className="card">
          <div className="card__body" style={{ color: 'var(--text-muted)' }}>
            Open a Territory and click <b>Generate Business Case Report</b> to view a printable engineering brief here, or pick two or more scenarios and click <b>Compare (N)</b> for a side-by-side analysis.
          </div>
        </div>
      </>
    );
  }

  if (isLoading || !plan) {
    return <div className="card"><div className="card__body">Loading report…</div></div>;
  }

  const s = plan.summary ?? {};
  const perAll: any[] = s.per_cluster ?? [];
  const per: any[] = buildFilter ? perAll.filter((_: any, i: number) => buildFilter.has(i)) : perAll;
  const edges: any[] = plan.backbone?.edges ?? [];
  const ftthCapexCatalog = per.reduce((a, c) => a + (c.capex ?? 0), 0);
  const bbCapex = s.total_backbone_capex ?? 0;
  const bbOpex = s.total_backbone_opex_monthly ?? 0;

  const allClusterCapexCatalog = perAll.reduce((a: number, c: any) => a + Number(c?.capex ?? 0), 0);
  const selectionRatio = allClusterCapexCatalog > 0 ? ftthCapexCatalog / allClusterCapexCatalog : 1;
  // ---- BEAD-priced grand total interpolated to current aerial mix (same as Builder) ----
  const laborOf = (pmObj: any): number => {
    const labors = (pmObj?.line_items || []).filter((l: any) =>
      l.category === 'Labor' && /construction labor/i.test(l.sku)
    );
    return labors.reduce((acc: number, l: any) => acc + (Number(l.cost) || 0), 0);
  };
  const beadGrandAerial   = Number(pmAerial?.totals?.grand_total ?? 0);
  const beadGrandBuried   = Number(pmBuried?.totals?.grand_total ?? 0);
  const beadDirectAerial  = Number(pmAerial?.totals?.direct ?? 0);
  const beadAddersAerial  = Number(pmAerial?.totals?.adders ?? 0);
  const beadAddersBuried  = Number(pmBuried?.totals?.adders ?? 0);
  const beadLaborAerial   = laborOf(pmAerial);
  const beadLaborBuried   = laborOf(pmBuried);
  const beadMaterialsEtc  = beadDirectAerial - beadLaborAerial;  // constant across slider
  const beadAerialFrac    = aerialMixPct / 100;
  const adjLaborCost = beadAerialFrac * beadLaborAerial + (1 - beadAerialFrac) * beadLaborBuried;
  const adjAdders    = beadAerialFrac * beadAddersAerial + (1 - beadAerialFrac) * beadAddersBuried;
  const adjBeadGrand = (beadMaterialsEtc + adjLaborCost + adjAdders) * selectionRatio;
  const beadLoaded = beadGrandAerial > 100_000 && beadGrandBuried > 100_000;
  const totalCapex = beadLoaded ? adjBeadGrand : (ftthCapexCatalog + bbCapex);
  const catalogGrand = ftthCapexCatalog + bbCapex;
  const beadScale = beadLoaded && catalogGrand > 0 ? totalCapex / catalogGrand : 1;
  const ftthCapex = ftthCapexCatalog * beadScale;

  // ---- MRR scaled to user's current take rates (segmented BEAD vs served) ----
  const totalPassings = per.reduce((a, c) => a + (c.passings ?? 0), 0);
  const baseTakeRate = 0.40;  // auto-design baseline
  const baseMrr = per.reduce((a, c) => a + (c.mrr ?? 0), 0);
  // Use the territory's BEAD fraction as the per-cluster proxy for the
  // BEAD/private split. plan.summary carries bead_in_territory.
  const beadInTerritory = Number(s.bead_in_territory ?? 0);
  const beadFraction = totalPassings > 0 ? Math.max(0, Math.min(1, beadInTerritory / totalPassings)) : 0;
  const baseMrrBead = baseMrr * beadFraction;
  const baseMrrPriv = baseMrr * (1 - beadFraction);
  const totalMrr =
    baseMrrBead * ((takeRateUnservedPct / 100) / baseTakeRate) +
    baseMrrPriv * ((takeRateServedPct   / 100) / baseTakeRate);
  const gm = totalMrr * margin;
  const paybackMo = gm > 0 ? totalCapex / gm : null;
  const costPerPassing = totalPassings > 0 ? totalCapex / totalPassings : 0;
  const yr5Tco = totalCapex + bbOpex * 60;
  const verdict = paybackMo == null ? 'WALK AWAY' : paybackMo <= 120 ? 'BUILD' : paybackMo <= 180 ? 'MARGINAL' : 'WALK AWAY';
  const verdictBg = verdict === 'BUILD' ? '#dcfce7' : verdict === 'MARGINAL' ? '#fef3c7' : '#fee2e2';
  const verdictFg = verdict === 'BUILD' ? '#047857' : verdict === 'MARGINAL' ? '#92400e' : '#b91c1c';

  const modeMiles = edges.reduce(
    (acc, e) => {
      const h = e.hybrid_result;
      if (h) {
        acc.fiber += h.fiber_build_miles || 0;
        acc.wireless += h.wireless_miles || 0;
        acc.ride_along += h.ride_along_miles || 0;
      }
      return acc;
    },
    { fiber: 0, wireless: 0, ride_along: 0 },
  );
  const bbTotalMi = (modeMiles.fiber + modeMiles.wireless + modeMiles.ride_along) || (s.backbone_length_mi ?? 1);

  const today = new Date();
  const reportDate = today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const docId = `MB-TPR-${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}-${(planId ?? '').slice(0,4).toUpperCase()}`;

  const aerialFrac = Math.max(0, Math.min(1, aerialMixPct / 100));
  const buriedFrac = 1 - aerialFrac;
  const aerialRate = soil?.aerial_per_ft != null ? Number(soil.aerial_per_ft) : null;
  const buriedSoil = Number(soil?.blended_labor_per_ft ?? 0);
  const buriedCat = Number(soil?.catalog_default_per_ft ?? 0);
  const blendedTotal = buriedSoil > 0 && aerialRate != null ? aerialFrac * aerialRate + buriedFrac * buriedSoil : null;
  const blendedCatalog = buriedCat > 0 && aerialRate != null ? aerialFrac * aerialRate + buriedFrac * buriedCat : null;
  const blendedAdj = blendedTotal != null && blendedCatalog != null ? blendedTotal - blendedCatalog : null;
  const wetlandTrigger = !!(soil?.wetland_risk?.needs_404_review);

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#0f172a', background: '#fff' }}>
      <style>{`
        @media print {
          body, html { background: #fff !important; }
          .no-print, .sidebar, .topbar, nav { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          h1, h2, h3 { page-break-after: avoid; }
          .section-block { page-break-inside: avoid; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={() => window.print()} style={{ background: '#0f172a', color: '#fff', border: 0, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>🖨️ Print / Save PDF</button>
        <button onClick={() => window.close()} style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Close</button>
      </div>

      {/* COVER */}
      <div style={{ borderTop: '4px solid #1e293b', borderBottom: '1px solid #1e293b', padding: '14px 0', marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 600 }}>
          {audience === 'investor' ? 'Investor Brief'
            : audience === 'sbo'    ? 'State Broadband Office Brief'
            : audience === 'exec'   ? 'Internal Executive Brief'
            : 'Engineering & Business Case Report'} — Territory Plan
        </div>
        <h1 style={{ margin: '6px 0 4px', fontSize: 26, lineHeight: 1.15 }}>{plan.name}</h1>
        <div style={{ color: '#475569', fontSize: 13 }}>
          {per.length} cluster{per.length === 1 ? '' : 's'} · {fmtNum(totalPassings)} passings · {Number(s.backbone_length_mi ?? 0).toFixed(1)} mi backbone · {fmtUSD(totalCapex)} capital plan
        </div>
      </div>
      <table style={{ width: '100%', fontSize: 11, marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ padding: '2px 8px 2px 0', color: '#64748b', width: 110 }}>Document ID</td>
            <td style={{ padding: '2px 8px', fontFamily: 'ui-monospace, monospace' }}>{docId}</td>
            <td style={{ padding: '2px 8px', color: '#64748b', width: 110 }}>Issued</td>
            <td style={{ padding: '2px 8px', fontWeight: 600 }}>{reportDate}</td>
          </tr>
          <tr>
            <td style={{ padding: '2px 8px 2px 0', color: '#64748b' }}>Plan ID</td>
            <td style={{ padding: '2px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>{plan.id}</td>
            <td style={{ padding: '2px 8px', color: '#64748b' }}>Pricing basis</td>
            <td style={{ padding: '2px 8px' }}>{beadLoaded ? 'BEAD project unit rates (per-plant)' : 'Catalog default'}</td>
          </tr>
          <tr>
            <td style={{ padding: '2px 8px 2px 0', color: '#64748b' }}>Prepared by</td>
            <td style={{ padding: '2px 8px', fontWeight: 600 }}>Milestone Build</td>
            <td style={{ padding: '2px 8px', color: '#64748b' }}>Source data</td>
            <td style={{ padding: '2px 8px' }}>USDA SSURGO · FCC Fabric · plant cost catalog</td>
          </tr>
        </tbody>
      </table>

      {/* EXECUTIVE SUMMARY */}
      <h2 style={SECTION_H}>1. Executive Summary</h2>
      <p style={{ fontSize: 12, lineHeight: 1.55, marginTop: 10 }}>
        This brief covers <strong>{plan.name}</strong>, a territory plan composed of <strong>{per.length}</strong> cluster{per.length === 1 ? '' : 's'} ({fmtNum(totalPassings)} BSL passings) connected by a <strong>{Number(s.backbone_length_mi ?? 0).toFixed(1)} mi</strong> hybrid backbone of fiber, point-to-point wireless, and ride-along across {edges.length} edge{edges.length === 1 ? '' : 's'}.
        Total engineered capital is <strong>{fmtUSD(totalCapex)}</strong> ({fmtUSD(costPerPassing)}/passing). Recurring revenue at steady state is <strong>{fmtUSD(totalMrr)}/mo</strong>; on a {(margin*100).toFixed(0)}% gross-margin basis, capital pays back in <strong>{fmtMo(paybackMo)}</strong> with a five-year TCO of {fmtUSD(yr5Tco)}.
        Construction labor is modeled at the <strong>{aerialMixPct}% aerial / {100 - aerialMixPct}% buried</strong> mix selected on Builder, with the buried portion weighted across SSURGO soil and bedrock conditions for the territory geometry (§3).
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        {[
          { label: 'Homes passed', value: fmtNum(totalPassings), sub: `${per.length} cluster${per.length === 1 ? '' : 's'}` },
          { label: 'Capital plan', value: fmtUSD(totalCapex), sub: beadLoaded ? 'BEAD-priced' : 'Catalog' },
          { label: 'FTTH capex', value: fmtUSD(ftthCapex), sub: `${fmtUSD(totalPassings ? ftthCapex/totalPassings : 0)}/pass` },
          { label: 'Backbone', value: fmtUSD(bbCapex * beadScale), sub: `${Number(s.backbone_length_mi ?? 0).toFixed(1)} mi · ${edges.length} edges` },
          { label: 'Recurring revenue', value: `${fmtUSD(totalMrr)}/mo`, sub: `GM @ ${(margin * 100).toFixed(0)}%` },
          { label: 'Payback (private)', value: fmtMo(paybackMo), sub: `5-yr TCO ${fmtUSD(yr5Tco)}` },
          // 2026-05-30 Phase 2.5: subsidy tile that adapts per grantDisplay choice
          (() => {
            const bead10yr = Number(plan.summary?.max_grant_available ?? 0);
            const rdofNominal = Number(rdofSummaryQ.data?.total_support_10yr_usd ?? 0);
            const rdofMonthly = Number(rdofSummaryQ.data?.total_monthly_support_usd ?? 0);
            // PV of annuity = monthly * [1 - (1+r)^-n] / r, monthly rate.
            const r = (discountRate / 100) / 12;
            const rdofNpv = r === 0 ? rdofNominal : rdofMonthly * (1 - Math.pow(1 + r, -120)) / r;
            if (grantDisplay === 'combined-nominal') {
              return { label: 'Total subsidy (nominal)', value: fmtUSD(bead10yr + rdofNominal),
                sub: `BEAD ${fmtUSD(bead10yr)} + RDOF ${fmtUSD(rdofNominal)} (10yr nom.)` };
            }
            if (grantDisplay === 'combined-npv') {
              return { label: `Total subsidy (NPV @ ${discountRate}%)`, value: fmtUSD(bead10yr + rdofNpv),
                sub: `BEAD ${fmtUSD(bead10yr)} + RDOF PV ${fmtUSD(rdofNpv)} of ${fmtUSD(rdofNominal)} 10yr` };
            }
            return { label: 'RDOF 10-yr stream', value: fmtUSD(rdofNominal),
              sub: rdofNominal > 0 ? `${fmtUSD(rdofMonthly)}/mo · BEAD shown separately` : 'none' };
          })(),
        ].map((k, i) => (
          <div key={i} style={{ flex: '1 1 145px', border: '1px solid #cbd5e1', borderRadius: 4, padding: '8px 10px', background: '#f8fafc' }}>
            <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{k.label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{k.sub}</div>
          </div>
        ))}
        <div style={{ flex: '0 0 auto', padding: '10px 16px', borderRadius: 4, background: verdictBg, color: verdictFg, alignSelf: 'stretch', display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 14, letterSpacing: '.04em' }}>
          {verdict}
        </div>
      </div>

      {/* 2026-05-30 Phase 2.5 — Federal Subsidy Attribution.
          Renders only when there's actual RDOF data (>0 passings). The
          section adapts to the grantDisplay choice from the report config
          modal (separate / combined-nominal / combined-npv). */}
      {(rdofSummaryQ.data?.total_rdof_passings ?? 0) > 0 && (() => {
        const bead10yr = Number(plan.summary?.max_grant_available ?? 0);
        const beadFed  = Number(plan.summary?.grant_federal ?? 0);
        const beadMat  = Number(plan.summary?.grant_match ?? 0);
        const rdofNominal = Number(rdofSummaryQ.data?.total_support_10yr_usd ?? 0);
        const rdofMonthly = Number(rdofSummaryQ.data?.total_monthly_support_usd ?? 0);
        const r = (discountRate / 100) / 12;
        const rdofNpv = r === 0 ? rdofNominal : rdofMonthly * (1 - Math.pow(1 + r, -120)) / r;
        const combinedNominal = bead10yr + rdofNominal;
        const combinedNpv     = bead10yr + rdofNpv;
        const perState = rdofSummaryQ.data?.per_state ?? [];

        return (
          <>
            <h2 style={SECTION_H}>Federal Subsidy Attribution</h2>
            <p style={{ fontSize: 12, lineHeight: 1.55, marginTop: 10 }}>
              This territory carries federal subsidy from <strong>{beadFed + beadMat > 0 ? 'BEAD' : ''}{(beadFed + beadMat > 0 && rdofNominal > 0) ? ' and ' : ''}{rdofNominal > 0 ? 'RDOF' : ''}</strong>.
              {' '}BEAD is a one-shot grant + match paid as construction reimbursement; RDOF is a monthly support stream paid over 10 years from FCC authorization. The two are <em>not</em> directly additive without choosing a convention.
            </p>

            {/* Display mode-aware summary tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
              <div style={{ border: '1px solid #1e40af', borderRadius: 6, padding: '10px 12px', background: '#eff6ff' }}>
                <div style={{ fontSize: 9, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>BEAD upfront (federal + match)</div>
                <div style={{ fontSize: 19, fontWeight: 800, color: '#1e3a8a', marginTop: 3 }}>{fmtUSD(bead10yr)}</div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Federal {fmtUSD(beadFed)} · Match {fmtUSD(beadMat)}</div>
              </div>
              <div style={{ border: '1px solid #0e7490', borderRadius: 6, padding: '10px 12px', background: '#ecfeff' }}>
                <div style={{ fontSize: 9, color: '#155e75', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>RDOF 10-yr stream (nominal)</div>
                <div style={{ fontSize: 19, fontWeight: 800, color: '#155e75', marginTop: 3 }}>{fmtUSD(rdofNominal)}</div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{fmtUSD(rdofMonthly)}/mo over 120 months</div>
              </div>
              {grantDisplay === 'combined-nominal' && (
                <div style={{ border: '2px solid #14532d', borderRadius: 6, padding: '10px 12px', background: '#dcfce7' }}>
                  <div style={{ fontSize: 9, color: '#14532d', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Total subsidy (nominal)</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: '#14532d', marginTop: 3 }}>{fmtUSD(combinedNominal)}</div>
                  <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>BEAD + RDOF, undiscounted</div>
                </div>
              )}
              {grantDisplay === 'combined-npv' && (
                <div style={{ border: '2px solid #14532d', borderRadius: 6, padding: '10px 12px', background: '#dcfce7' }}>
                  <div style={{ fontSize: 9, color: '#14532d', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Total subsidy (NPV @ {discountRate}%)</div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: '#14532d', marginTop: 3 }}>{fmtUSD(combinedNpv)}</div>
                  <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>BEAD {fmtUSD(bead10yr)} + RDOF PV {fmtUSD(rdofNpv)} (of {fmtUSD(rdofNominal)} nominal)</div>
                </div>
              )}
            </div>

            {/* Per-state RDOF breakdown */}
            {perState.length > 0 && (
              <>
                <h3 style={SUB_H}>Per-state RDOF attribution</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #1e293b' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>State</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>Provider</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>SAC</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>Passings (terr / award)</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>$/loc/10yr</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>This territory (10yr)</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#475569' }}>Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perState.map((ps: any) => (
                      <tr key={`${ps.state}-${ps.sac}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{ps.state}</td>
                        <td style={{ padding: '6px 8px' }}>{ps.provider}</td>
                        <td style={{ padding: '6px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{ps.sac}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(ps.rdof_passings)} / {fmtNum(ps.award_loc_count)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${ps.dollars_per_loc_10yr.toFixed(2)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtUSD(ps.cluster_contribution_10yr_usd)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(ps.cluster_contribution_monthly_usd)}/mo</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #1e293b', background: '#f1f5f9', fontWeight: 700 }}>
                      <td style={{ padding: '8px 8px' }} colSpan={3}>Total</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(rdofSummaryQ.data?.total_rdof_passings ?? 0)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: '#64748b' }}>—</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(rdofNominal)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(rdofMonthly)}/mo</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <p style={{ marginTop: 12, fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
              <strong>Caveats:</strong> RDOF attribution is a pro-rata estimate (territory passings / award CAM count × total 10-year support). Per FCC 47 CFR § 54.802(c)(1), final support is trued up at year 6 against the Broadband Serviceable Location Fabric — actual contribution may shift if the territory's Fabric count falls below 65% of CAM (automatic pro-rata cut) or above 135% (discretionary petition for additional support). NPV uses present-value-of-annuity at the chosen discount rate over 120 monthly payments. BEAD and RDOF are mutually exclusive at the location level per NTIA reason code 4; overlap detection is on the roadmap.
            </p>
          </>
        );
      })()}

      {/* ASSUMPTIONS */}
      <h2 style={SECTION_H}>2. Assumptions & Inputs</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 12 }}>
        <tbody>
          <tr><td style={{ padding: '4px 8px', color: '#64748b', width: '38%' }}>Take rate · BEAD (unserved)</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{takeRateUnservedPct}% <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>(industry baseline 60%)</span></td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Take rate · already-served</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{takeRateServedPct}% <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>(industry baseline 25%)</span></td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>BEAD passing fraction</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{(beadFraction*100).toFixed(0)}% <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>(territory split applied to MRR scaling)</span></td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Gross margin</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{(margin*100).toFixed(0)}%</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Aerial / buried construction mix</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{aerialMixPct}% aerial · {100 - aerialMixPct}% buried</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Aerial labor (catalog)</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{aerialRate != null ? fmt2(aerialRate) : '— (missing)'}/ft</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Buried labor</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>SSURGO-weighted soil blend (§3)</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Pricing source</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{beadLoaded ? 'BEAD project unit rates from plant cost catalog' : 'Catalog defaults (BEAD pricing not loaded)'}</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Backbone topology</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>Hybrid optimizer · {edges.length} edge{edges.length === 1 ? '' : 's'} · cheapest mix per leg</td></tr>
          <tr><td style={{ padding: '4px 8px', color: '#64748b' }}>Cluster filter applied</td><td style={{ padding: '4px 8px', fontWeight: 600 }}>{buildFilter ? `${per.length} of ${perAll.length} clusters selected for build` : 'All clusters'}</td></tr>
        </tbody>
      </table>

      {/* CONSTRUCTION PROFILE */}
      <h2 style={SECTION_H}>3. Construction Profile (SSURGO Soil-Aware)</h2>
      {!soil || !soil.construction_method_breakdown || !soil.soil_profile ? (
        <div style={{ fontSize: 11, color: '#94a3b8', padding: '8px 0' }}>
          No SSURGO soil data available for this territory geometry. Construction labor falls back to plant catalog defaults.
        </div>
      ) : (
        <>
          <h3 style={SUB_H}>3.1 Soil Conditions ({soil.soil_profile.map_unit_count.toLocaleString()} SSURGO map units · {soil.weighted_by === 'route' && soil.route_length_m ? `route-weighted (${(soil.route_length_m/1609.34).toFixed(1)} mi of fiber)` : soil.weighted_by === 'area_fallback' ? 'area-weighted (no fiber yet)' : 'area-weighted'})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 6 }}>
            <tbody>
              <tr>
                <td style={{ padding: '3px 8px', color: '#64748b', width: 150 }}>Dominant drainage</td>
                <td style={{ padding: '3px 8px', fontWeight: 600 }}>{soil.soil_profile.dominant_drainage ?? '—'}</td>
                <td style={{ padding: '3px 8px', color: '#64748b', width: 150 }}>Hydrologic group</td>
                <td style={{ padding: '3px 8px', fontWeight: 600 }}>{soil.soil_profile.dominant_hydro_group ?? '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px', color: '#64748b' }}>Avg slope</td>
                <td style={{ padding: '3px 8px', fontWeight: 600 }}>{soil.soil_profile.avg_slope_pct == null ? '—' : `${soil.soil_profile.avg_slope_pct.toFixed(1)}%`}</td>
                <td style={{ padding: '3px 8px', color: '#64748b' }}>Avg bedrock depth</td>
                <td style={{ padding: '3px 8px', fontWeight: 600 }}>{soil.soil_profile.avg_bedrock_depth_cm == null ? '—' : `${(soil.soil_profile.avg_bedrock_depth_cm / 2.54).toFixed(0)} in`}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px', color: '#64748b' }}>Hydric (weighted)</td>
                <td style={{ padding: '3px 8px', fontWeight: 600 }}>{soil.soil_profile.hydric_pct_weighted == null ? '—' : `${soil.soil_profile.hydric_pct_weighted.toFixed(1)}%`}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>

          {soil.soil_profile.top_soils && soil.soil_profile.top_soils.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600, marginTop: 12, marginBottom: 4 }}>
                Dominant soil units
              </div>
              <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                <tbody>
                  {soil.soil_profile.top_soils.slice(0, 5).map((t, i) => (
                    <tr key={i} style={{ borderTop: i === 0 ? 0 : '1px solid #e2e8f0' }}>
                      <td style={{ padding: '2px 8px', color: '#64748b', width: 60, fontVariantNumeric: 'tabular-nums', fontFamily: 'ui-monospace, monospace' }}>{t.musym || '—'}</td>
                      <td style={{ padding: '2px 8px' }}>{t.muname}</td>
                      <td style={{ padding: '2px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{t.area_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h3 style={SUB_H}>3.2 Construction Method Plan (buried route, area-weighted)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 6 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#475569' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', width: 150 }}>Method</th>
                <th style={{ padding: '4px 8px', textAlign: 'left' }}>% of buried route</th>
                <th style={{ padding: '4px 8px', textAlign: 'right', width: 90 }}>Labor $/ft</th>
                <th style={{ padding: '4px 8px', textAlign: 'right', width: 140 }}>Contribution to blend</th>
              </tr>
            </thead>
            <tbody>
              {(['plow','trench','rock_saw','directional_bore'] as const).map((k) => {
                const r = soil.construction_method_breakdown?.[k]; if (!r) return null;
                const pct = r.pct ?? 0;
                const rate = r.labor_per_ft ?? 0;
                const contrib = (pct / 100) * rate;
                const colors: Record<string, string> = { plow: '#3b82f6', trench: '#22c55e', rock_saw: '#f59e0b', directional_bore: '#ef4444' };
                const names: Record<string, string> = { plow: 'Plow / vibratory plow', trench: 'Trench (with dewater)', rock_saw: 'Rock saw / trencher', directional_bore: 'Directional bore (HDD)' };
                return (
                  <tr key={k} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{names[k]}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: colors[k] }} />
                        </div>
                        <span style={{ width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt2(rate)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{fmt2(contrib)}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '1.5px solid #94a3b8', background: '#f8fafc' }}>
                <td colSpan={3} style={{ padding: '5px 8px', textAlign: 'right', color: '#475569' }}>Buried-soil weighted blend</td>
                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt2(buriedSoil)}/ft</td>
              </tr>
            </tbody>
          </table>

          <h3 style={SUB_H}>3.3 Aerial / Buried Blend (engineering basis for project labor)</h3>
          <table style={{ width: '100%', fontSize: 11, marginTop: 6, borderCollapse: 'collapse', border: '1px solid #cbd5e1', background: '#f8fafc' }}>
            <tbody>
              <tr>
                <td style={{ padding: '4px 10px', color: '#475569' }}>Aerial portion ({(aerialFrac*100).toFixed(0)}% × {aerialRate != null ? fmt2(aerialRate) : '—'}/ft catalog)</td>
                <td style={{ padding: '4px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{aerialRate != null ? fmt2(aerialFrac * aerialRate) : '—'}/ft</td>
              </tr>
              <tr>
                <td style={{ padding: '4px 10px', color: '#475569' }}>Buried portion ({(buriedFrac*100).toFixed(0)}% × {fmt2(buriedSoil)}/ft soil-blend)</td>
                <td style={{ padding: '4px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt2(buriedFrac * buriedSoil)}/ft</td>
              </tr>
              <tr style={{ borderTop: '1px solid #cbd5e1' }}>
                <td style={{ padding: '5px 10px', fontWeight: 700 }}>Total blended labor</td>
                <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{blendedTotal != null ? `${fmt2(blendedTotal)}/ft` : '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 10px', color: '#64748b', fontSize: 10 }}>vs catalog blend ({blendedCatalog != null ? `${fmt2(blendedCatalog)}/ft` : '—'})</td>
                <td style={{ padding: '3px 10px', textAlign: 'right', fontSize: 10, fontVariantNumeric: 'tabular-nums',
                  color: blendedAdj == null ? '#64748b' : blendedAdj > 0.5 ? '#b45309' : blendedAdj < -0.5 ? '#047857' : '#64748b' }}>
                  {blendedAdj == null ? '—' : `${blendedAdj >= 0 ? '+' : ''}${fmt2(blendedAdj)}/ft`}
                </td>
              </tr>
            </tbody>
          </table>

          {soil.wetland_risk?.needs_404_review && (
            <div style={{ marginTop: 10, padding: '6px 10px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, fontSize: 11 }}>
              <strong style={{ color: '#92400e' }}>⚠ §404 review may be required.</strong>
              <span style={{ marginLeft: 6, color: '#92400e' }}>
                {soil.wetland_risk.hydric_majority_area_pct.toFixed(1)}% of territory is hydric ({soil.wetland_risk.hydric_polygon_count.toLocaleString()} polygons).
                Triggers FED-ENV-USACE obligation; allow 30–90 day USACE response window.
              </span>
            </div>
          )}
        </>
      )}

      {/* COMPLIANCE FOOTPRINT */}
      <h2 style={SECTION_H}>4. Compliance & Permitting Footprint</h2>
      <div style={{ fontSize: 11, color: '#475569', marginTop: 8, marginBottom: 6 }}>
        Federal obligations triggered by this build profile (apply once a BEAD project is awarded against this territory plan):
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: '#f8fafc', color: '#475569' }}>
            <th style={{ padding: '4px 8px', textAlign: 'left', width: 180 }}>Obligation</th>
            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Trigger</th>
            <th style={{ padding: '4px 8px', textAlign: 'left', width: 130 }}>Cadence</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderTop: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>FED-LAB-WH347</td>
            <td style={{ padding: '4px 8px' }}>Davis-Bacon Act — federally funded construction</td>
            <td style={{ padding: '4px 8px', color: '#475569' }}>Weekly during build</td>
          </tr>
          <tr style={{ borderTop: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>FED-MAT-BABA</td>
            <td style={{ padding: '4px 8px' }}>Build America, Buy America — manufacturer cert per material per shipment</td>
            <td style={{ padding: '4px 8px', color: '#475569' }}>Per shipment</td>
          </tr>
          <tr style={{ borderTop: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>FED-ENV-NEPA</td>
            <td style={{ padding: '4px 8px' }}>NEPA categorical exclusion / EA / EIS based on disturbance footprint</td>
            <td style={{ padding: '4px 8px', color: '#475569' }}>Pre-construction</td>
          </tr>
          {wetlandTrigger && (
            <tr style={{ borderTop: '1px solid #e2e8f0', background: '#fffbeb' }}>
              <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10, color: '#92400e' }}>FED-ENV-USACE</td>
              <td style={{ padding: '4px 8px', color: '#92400e' }}><strong>USACE §404 review</strong> — hydric soil &gt; 50% in {soil?.wetland_risk?.hydric_polygon_count} polygon{(soil?.wetland_risk?.hydric_polygon_count ?? 0) === 1 ? '' : 's'} ({soil?.wetland_risk?.hydric_majority_area_pct.toFixed(1)}% of territory)</td>
              <td style={{ padding: '4px 8px', color: '#92400e' }}>Pre-construction (30–90 day window)</td>
            </tr>
          )}
          <tr style={{ borderTop: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>FED-ENV-FLOOD</td>
            <td style={{ padding: '4px 8px' }}>Floodplain assessment per FEMA mapping (8 Step Process if floodway crossing)</td>
            <td style={{ padding: '4px 8px', color: '#475569' }}>Pre-construction</td>
          </tr>
          <tr style={{ borderTop: '1px solid #e2e8f0' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>SVC-LCSO-OFFER</td>
            <td style={{ padding: '4px 8px' }}>Low-cost service option offered to eligible subscribers</td>
            <td style={{ padding: '4px 8px', color: '#475569' }}>Continuous post-build</td>
          </tr>
        </tbody>
      </table>

      {/* NETWORK ENGINEERING */}
      <h2 style={SECTION_H}>5. Network Engineering — Backbone</h2>
      <div style={{ marginTop: 8, fontSize: 12, color: '#475569', marginBottom: 8 }}>
        Hybrid backhaul: the optimizer picked the cheapest mix of fiber build, point-to-point wireless, and ride-along on existing towers/fiber per edge. Total {bbTotalMi.toFixed(1)} mi across {edges.length} edge{edges.length === 1 ? '' : 's'}.
      </div>
      <StackedBar modeMiles={modeMiles} totalMi={bbTotalMi} tall />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 14 }}>
        <ModeStat color={MODE_COLOR.fiber_build} label="Fiber build" miles={modeMiles.fiber} total={bbTotalMi} note="New underground/aerial fiber" />
        <ModeStat color={MODE_COLOR.wireless_ptp} label="Wireless PtP" miles={modeMiles.wireless} total={bbTotalMi} note="Microwave links between towers" />
        <ModeStat color={MODE_COLOR.ride_along} label="Ride-along" miles={modeMiles.ride_along} total={bbTotalMi} note="Lease capacity on existing fiber" />
      </div>

      <h3 style={SUB_H}>5.1 Backbone Edges ({edges.length})</h3>
      {edges.map((e, i) => {
        const h = e.hybrid_result;
        const total = e.road_distance_mi ?? e.straight_distance_mi ?? h?.total_distance_mi ?? 0;
        return (
          <div key={i} className="section-block" style={{ padding: '10px 0', borderBottom: i < edges.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>Edge {i+1}: {e.from_name} → {e.to_name}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                {total.toFixed(1)} mi · {h ? <b style={{ color: '#0f172a' }}>{fmtUSD(h.total_capex)}</b> : '—'}
                {h && h.total_opex_monthly > 0 && <span> + {fmtUSD(h.total_opex_monthly)}/mo</span>}
              </div>
            </div>
            {h && <EdgeBar segments={h.segments} totalMi={h.total_distance_mi} />}
            {h && (
              <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 11, flexWrap: 'wrap', color: '#475569' }}>
                {h.fiber_build_miles > 0 && <span><Dot color={MODE_COLOR.fiber_build} /><b>{h.fiber_build_miles.toFixed(1)} mi</b> fiber build</span>}
                {h.wireless_miles > 0 && <span><Dot color={MODE_COLOR.wireless_ptp} /><b>{h.wireless_miles.toFixed(1)} mi</b> wireless ({h.wireless_hops} hop{h.wireless_hops === 1 ? '' : 's'})</span>}
                {h.ride_along_miles > 0 && <span><Dot color={MODE_COLOR.ride_along} /><b>{h.ride_along_miles.toFixed(1)} mi</b> ride-along</span>}
                {h.min_throughput_mbps != null && <span style={{ marginLeft: 'auto' }}>bottleneck {h.min_throughput_mbps} Mbps</span>}
              </div>
            )}
            {e.error && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>{e.error}</div>}
          </div>
        );
      })}

      {/* CLUSTER BUILD PLAN */}
      <h2 style={SECTION_H}>6. Cluster Build Plan ({per.length})</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Cluster</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Passings</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Capex</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>$/passing</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>MRR</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Payback</th>
          </tr>
        </thead>
        <tbody>
          {per.map((c, i) => {
            const cCapex = Number(c.capex ?? 0) * beadScale;
            // Apply territory-level BEAD fraction to scale segments;
            // per-cluster BEAD/private split isn't available at report layer.
            const cBaseMrrBead = Number(c.mrr ?? 0) * beadFraction;
            const cBaseMrrPriv = Number(c.mrr ?? 0) * (1 - beadFraction);
            const cMrr = cBaseMrrBead * ((takeRateUnservedPct / 100) / baseTakeRate) +
                         cBaseMrrPriv * ((takeRateServedPct   / 100) / baseTakeRate);
            const cb     = (cMrr > 0 ? cCapex / (cMrr * margin) : 999);
            return (
              <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{c.name}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(c.passings)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(cCapex)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.passings > 0 ? fmtUSD(cCapex / c.passings) : '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(cMrr)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: cb < 36 ? '#059669' : cb < 60 ? '#d97706' : '#b91c1c', fontWeight: 600 }}>{fmtMo(cMrr > 0 ? cCapex / (cMrr * margin) : null)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f1f5f9', fontWeight: 700, borderTop: '2px solid #94a3b8' }}>
            <td style={{ padding: '6px 8px' }}>Total</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(totalPassings)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(ftthCapex)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{totalPassings ? fmtUSD(ftthCapex/totalPassings) : '—'}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(totalMrr)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMo(paybackMo)}</td>
          </tr>
        </tfoot>
      </table>

      {/* NOTES */}
      <h2 style={SECTION_H}>7. Notes & Reference Standards</h2>
      <ul style={{ fontSize: 10.5, color: '#475569', lineHeight: 1.7, paddingLeft: 18, marginTop: 8 }}>
        <li><strong>SSURGO</strong> (USDA-NRCS Soil Survey Geographic Database) — area-weighted intersect of territory geometry with <code>ssurgo.mupolygon</code> ⨝ <code>ssurgo.muaggatt</code>. Construction-method classification uses thresholds from <code>plant_settings.placement.soil_thresholds</code> (default: hydric ≥ 50% → directional bore; bedrock ≤ 18 in → rock saw; group D / poor drainage / slope &gt; 15% → trench; group A/B with bedrock ≥ 36 in and slope &lt; 10% → plow).</li>
        <li><strong>Per-method labor $/ft</strong> from <code>cost_catalog.labor.per_ft.*</code> × plant <code>region_mult</code>, with optional per-plant overrides via <code>plant_settings.placement.soil_rates</code>.</li>
        <li><strong>Aerial labor</strong> uses the plant catalog default ({aerialRate != null ? fmt2(aerialRate) : '—'}/ft); the {aerialMixPct}/{100 - aerialMixPct} mix is the value selected on Builder when this report was generated.</li>
        <li><strong>Backbone topology</strong> from the hybrid optimizer: per-edge cheapest combination of new fiber build, wireless PtP between towers, and ride-along on existing fiber, subject to throughput constraint per leg.</li>
        <li><strong>Cluster economics</strong>: capex priced from {beadLoaded ? 'BEAD project unit rates (per-plant)' : 'plant cost catalog defaults'}; MRR from per-cluster take-rate × ARPU.</li>
        <li><strong>NTIA SAR 2.0</strong> reporting categories — <code>broadbandusa.ntia.gov/sites/default/files/2024-12/BEAD_Anticipated_SAR_Reporting_Requirements_Guidance.pdf</code></li>
        <li><strong>NTIA Performance Measures</strong> (speed/latency/loss methodology) — <code>broadbandusa.ntia.gov/sites/default/files/2025-09/Performance_Measures_Policy_Notice.pdf</code></li>
        <li><strong>Hydric soils &gt; 50%</strong> trigger USACE §404 review per Clean Water Act (FED-ENV-USACE); allow 30–90 day response window.</li>
        <li><strong>Verdict</strong>: BUILD ≤ 120 mo payback · MARGINAL 120–180 mo · WALK AWAY &gt; 180 mo. Payback computed on total capex ÷ (MRR × gross margin).</li>
      </ul>

      <div style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: 9 }}>
        <span>Generated by Milestone Build · {docId}</span>
        <span>{reportDate} · Plan {plan.id}</span>
      </div>
    </div>
  );
}

function Dot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: color, marginRight: 6, verticalAlign: 'middle' }} />;
}

function ModeStat({ color, label, miles, total, note }: { color: string; label: string; miles: number; total: number; note: string }) {
  const pct = total > 0 ? (miles / total) * 100 : 0;
  return (
    <div style={{ padding: 10, background: '#f8fafc', borderRadius: 4, borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
        {miles.toFixed(1)} <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>mi ({pct.toFixed(0)}%)</span>
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{note}</div>
    </div>
  );
}

function StackedBar({ modeMiles, totalMi, tall }: { modeMiles: { fiber: number; wireless: number; ride_along: number }; totalMi: number; tall?: boolean }) {
  const t = totalMi || 1;
  const h = tall ? 18 : 10;
  return (
    <div style={{ display: 'flex', height: h, borderRadius: h / 2, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
      {modeMiles.fiber > 0 && <div style={{ width: `${(modeMiles.fiber / t) * 100}%`, background: MODE_COLOR.fiber_build }} title={`Fiber ${modeMiles.fiber.toFixed(1)} mi`} />}
      {modeMiles.wireless > 0 && <div style={{ width: `${(modeMiles.wireless / t) * 100}%`, background: MODE_COLOR.wireless_ptp }} title={`Wireless ${modeMiles.wireless.toFixed(1)} mi`} />}
      {modeMiles.ride_along > 0 && <div style={{ width: `${(modeMiles.ride_along / t) * 100}%`, background: MODE_COLOR.ride_along }} title={`Ride-along ${modeMiles.ride_along.toFixed(1)} mi`} />}
    </div>
  );
}

function EdgeBar({ segments, totalMi }: { segments: any[]; totalMi: number }) {
  const t = totalMi || 1;
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
      {segments.map((seg, i) => (
        <div
          key={i}
          style={{
            width: `${(seg.distance_mi / t) * 100}%`,
            background: MODE_COLOR[seg.mode] || '#94a3b8',
            borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.4)' : 'none',
          }}
          title={`${MODE_LABEL[seg.mode]} · ${seg.distance_mi.toFixed(2)} mi · ${fmtUSD(seg.capex)}`}
        />
      ))}
    </div>
  );
}

function ScenarioComparison({ planIds, margin }: { planIds: string[]; margin: number }) {
  const queries = planIds.map((id) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<any>({
      queryKey: ['territory-plan-report', id],
      queryFn: () => api.get(`/territory/plans/${id}`),
      enabled: !!id,
    });
  });
  const loading = queries.some((q) => q.isLoading);
  const plans: any[] = queries.map((q) => q.data).filter(Boolean);
  if (loading) return <div className="card"><div className="card__body">Loading {planIds.length} scenarios…</div></div>;
  if (plans.length === 0) return <div className="card"><div className="card__body">No scenarios loaded.</div></div>;
  const metric = (p: any, path: (s: any) => number | null | undefined): number | null => {
    const v = path(p.summary ?? {});
    return v == null || !isFinite(v as number) ? null : (v as number);
  };
  const rows: Array<{ label: string; get: (p: any) => string }> = [
    { label: 'Status', get: (p) => String(p.status ?? '—') },
    { label: 'Passings', get: (p) => fmtNum(metric(p, (s) => s.total_passings) ?? 0) },
    { label: 'BEAD in territory', get: (p) => fmtNum(metric(p, (s) => s.bead_in_territory) ?? 0) },
    { label: 'BEAD uncovered', get: (p) => { const u = metric(p, (s) => s.bead_uncovered) ?? 0; return u === 0 ? '0 ✓' : String(u); }},
    { label: 'FTTH CAPEX', get: (p) => fmtUSD(metric(p, (s) => s.total_cluster_capex) ?? 0) },
    { label: 'Backbone CAPEX', get: (p) => fmtUSD(metric(p, (s) => s.total_backbone_capex) ?? 0) },
    { label: 'Total CAPEX', get: (p) => fmtUSD((metric(p, (s) => s.total_cluster_capex) ?? 0) + (metric(p, (s) => s.total_backbone_capex) ?? 0)) },
    { label: 'Grant available', get: (p) => fmtUSD(metric(p, (s) => s.max_grant_available) ?? 0) },
    { label: 'Private capital', get: (p) => { const total = (metric(p, (s) => s.total_cluster_capex) ?? 0) + (metric(p, (s) => s.total_backbone_capex) ?? 0); const grant = metric(p, (s) => s.max_grant_available) ?? 0; return fmtUSD(Math.max(0, total - grant)); }},
    { label: 'MRR', get: (p) => `${fmtUSD(metric(p, (s) => s.total_mrr) ?? 0)}/mo` },
    { label: 'GM @' + Math.round(margin * 100) + '%', get: (p) => `${fmtUSD((metric(p, (s) => s.total_mrr) ?? 0) * margin)}/mo` },
    { label: 'Payback', get: (p) => { const capex = (metric(p, (s) => s.total_cluster_capex) ?? 0) + (metric(p, (s) => s.total_backbone_capex) ?? 0); const gm = (metric(p, (s) => s.total_mrr) ?? 0) * margin; return gm > 0 ? fmtMo(capex / gm) : '—'; }},
    { label: 'Cost / passing', get: (p) => { const capex = (metric(p, (s) => s.total_cluster_capex) ?? 0) + (metric(p, (s) => s.total_backbone_capex) ?? 0); const passings = metric(p, (s) => s.total_passings) ?? 0; return passings > 0 ? fmtUSD(capex / passings) : '—'; }},
    { label: 'Backbone length', get: (p) => `${(metric(p, (s) => s.backbone_length_mi) ?? 0).toFixed(1)} mi` },
    { label: 'Backbone edges', get: (p) => String(metric(p, (s) => s.backbone_edges) ?? 0) },
    { label: 'Clusters', get: (p) => String((p.clusters ?? []).length) },
  ];
  const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', background: '#0f172a', color: '#fff', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #e2e8f0', fontSize: 13, whiteSpace: 'nowrap' };
  return (
    <>
      <div className="page__header no-print">
        <div>
          <h1 className="page__title">Scenario comparison</h1>
          <div className="page__subtitle">Side-by-side business case across {plans.length} scenarios</div>
        </div>
        <div><button onClick={() => window.print()} className="btn btn--primary" style={{ padding: '8px 14px' }}>Print / Save PDF</button></div>
      </div>
      <div className="card">
        <div className="card__body" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, position: 'sticky', left: 0, zIndex: 1 }}>Metric</th>
                {plans.map((p) => (<th key={p.id} style={th}>{p.name}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                  <td style={{ ...td, fontWeight: 600, position: 'sticky', left: 0, background: i % 2 === 0 ? '#f8fafc' : '#fff', zIndex: 1 }}>{row.label}</td>
                  {plans.map((p) => (<td key={p.id} style={td}>{row.get(p)}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ height: 12 }} />
      <div className="card">
        <div className="card__header">Notes</div>
        <div className="card__body" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Metrics use the current Gross Margin ({Math.round(margin * 100)}%) for payback and GM/mo. BEAD coverage is measured spatially: a BEAD-awarded location is counted as covered when it sits within 150 m of any planned fiber in the plan.
        </div>
      </div>
    </>
  );
}
